import React, { useEffect, useState, useRef, useReducer, useMemo, useCallback } from 'react';
import DeviceCard from './DeviceCard.jsx';
import HelpModal from './HelpModal.jsx';
import DeviceDetailsPanel from './DeviceDetailsPanel.jsx';
import MultiSelect from './MultiSelect.jsx';
import DeviceSelectorModal from './DeviceSelectorModal.jsx';
import { ToastContainer } from 'react-toastify';
import pako from 'pako';
import 'react-toastify/dist/ReactToastify.css';
import { getValidStoredToken } from './utils/auth';
import { devicesReducer } from './reducers/devicesReducer';
import { PRODUCT_NAMES, SYSTEM_TYPES } from './constants';
import { getSystemTypeForCore } from './utils/systemTypeCache';
import './Dashboard.css';

// Helper: resolve human-readable system type using live aggregatedData or cached numeric code.
function resolveSystemType(device) {
  if (!device) return 'Unknown';
  // Prefer current aggregatedData numeric code if present.
  const direct = device.aggregatedData && device.aggregatedData['r/system_type'];
  // Fallback to cached raw code (also numeric as string) if direct missing.
  const cached = !direct ? getSystemTypeForCore(device.coreid) : undefined;
  const rawCode = direct ?? cached;
  if (rawCode === undefined || rawCode === null || rawCode === '') return 'Unknown';
  const numeric = Number(rawCode);
  if (Number.isNaN(numeric)) return 'Unknown';
  return SYSTEM_TYPES[numeric] || 'Unknown';
}

const Dashboard = ({ onAuthExpired }) => {
  const [devices, dispatch] = useReducer(devicesReducer, {}); // Managed by reducer
  const [ws, setWs] = useState(null);
  const [isSubscribed, setIsSubscribed] = useState(false);
  const [visibleDeviceIds, setVisibleDeviceIds] = useState([]); // State to store visible device IDs
  const [subscribeAll, setSubscribeAll] = useState(false); // Track if subscribing to all devices
  // Filtering / sorting state
  const [filterText, setFilterText] = useState('');
  const [filterStatus, setFilterStatus] = useState('any'); // any|online|offline|stale
  const [filterSystemType, setFilterSystemType] = useState([]); // Array of selected system types
  const [filterProductFamily, setFilterProductFamily] = useState('any'); // any|Ori v1|Ori v2
  const [filterFirmware, setFilterFirmware] = useState([]); // Array of selected firmware versions
  const [hideStale, setHideStale] = useState(false);
  const [sortBy, setSortBy] = useState('last_heard'); // last_heard|coreid|system_type|product|movement
  const [sortDir, setSortDir] = useState('desc'); // asc|desc
  // Selected device for details panel
  const [selectedDeviceId, setSelectedDeviceId] = useState(null); // Track which device is selected for details panel
  // Subscription form state
  const [deviceIds, setDeviceIds] = useState('');
  const [subscribeAllDevices, setSubscribeAllDevices] = useState(false);
  const [initializeAll, setInitializeAll] = useState(true);
  // System count state
  const [systemCount, setSystemCount] = useState({ filtered: 0, total: 0 });
  const [showHelp, setShowHelp] = useState(false);
  const [showDeviceSelector, setShowDeviceSelector] = useState(false);
  const [animationsEnabled, setAnimationsEnabled] = useState(true);
  //const [initialized, setInitialized] = useState(false); // Track if the initial state has been received
  //const [subscribedDeviceIds, setSubscribedDeviceIds] = useState([]); // State to store the device IDs being subscribed to

  // Function to establish the WebSocket connection
  const isConnectingRef = useRef(false);
  const isSubscribedRef = useRef(false);
  const compressedChunksRef = useRef(new Map()); // key: batchIndex, value: { chunks: [], total: num }
  const sfdcCompressedChunksRef = useRef(new Map()); // For SFDC data chunks

  const markSubscribed = (value) => {
    isSubscribedRef.current = value;
    setIsSubscribed(value);
  };

  const connectWebSocket = (idToken = null) => {
    if (isConnectingRef.current || (ws && ws.readyState === WebSocket.OPEN)) return;
    isConnectingRef.current = true;

    console.log("Connecting WebSocket");

    const token = idToken || getValidStoredToken();
    if (!token) {
      // force re-login UI
      console.log('ID token missing/expired. Prompting login.');
      onAuthExpired?.();
      return;
    }

    const socket = new WebSocket(`wss://rt1igglbs1.execute-api.us-east-1.amazonaws.com/production/?token=${token}`);

    socket.onopen = () => {
      console.log('Connected to WebSocket');
      isConnectingRef.current = false;
      setWs(socket); // Set WebSocket instance after successful connection
      // Start heartbeat
      const interval = setInterval(() => {
        if (socket.readyState === WebSocket.OPEN) {
          //console.log('Sending heartbeat ping');
          socket.send(JSON.stringify({ action: '/ping' }));
        }
      }, 540000); // 9 minutes
      // Store interval ID on socket for cleanup
      socket._heartbeat = interval;
    };

    socket.onmessage = (event) => {
      try {
        const receivedData = JSON.parse(event.data);

        if (receivedData.message && receivedData.message === "Forbidden") {
          console.warn('Received forbidden message:', receivedData);
          return; // Ignore this message
        }

        // Handle SFDC systems loaded message (for device selector)
        if (receivedData.event === "sfdc_systems_loaded") {
          // Check if this is compressed chunked data
          if (receivedData.compressed && receivedData.compressedChunk) {
            const { snapshotBatchIndex, compressedChunkIndex, compressedChunkTotal, compressedChunk } = receivedData;
            const batchKey = `sfdc_${snapshotBatchIndex || 0}`;

            if (!sfdcCompressedChunksRef.current.has(batchKey)) {
              sfdcCompressedChunksRef.current.set(batchKey, {
                chunks: new Array(compressedChunkTotal).fill(null),
                total: compressedChunkTotal,
                received: 0
              });
            }

            const batch = sfdcCompressedChunksRef.current.get(batchKey);

            if (batch.chunks[compressedChunkIndex] === null) {
              batch.chunks[compressedChunkIndex] = compressedChunk;
              batch.received++;
              console.log(`Received SFDC compressed chunk ${compressedChunkIndex + 1}/${compressedChunkTotal} for batch ${snapshotBatchIndex} (total received: ${batch.received})`);
            }

            // Check if we have all chunks
            if (batch.received === batch.total) {
              console.log(`All SFDC chunks received for batch ${snapshotBatchIndex}, decompressing...`);
              try {
                const fullCompressed = batch.chunks.join('');
                const binaryString = atob(fullCompressed);
                const bytes = new Uint8Array(binaryString.length);
                for (let i = 0; i < binaryString.length; i++) {
                  bytes[i] = binaryString.charCodeAt(i);
                }
                const decompressed = pako.ungzip(bytes, { to: 'string' });
                const data = JSON.parse(decompressed);

                console.log(`Successfully decompressed SFDC data: ${data.buildings?.length} buildings`);
                window.dispatchEvent(new CustomEvent('sfdcSystemsLoaded', { detail: data }));
                sfdcCompressedChunksRef.current.delete(batchKey);
              } catch (error) {
                console.error('Error decompressing SFDC chunk data:', error);
              }
            }
            return;
          }

          // Handle uncompressed SFDC data
          console.log('Received SFDC systems data:', receivedData.buildings?.length, 'buildings');
          window.dispatchEvent(new CustomEvent('sfdcSystemsLoaded', { detail: receivedData }));
          return;
        } else if (!isSubscribedRef.current) {
          console.warn('Received message but not subscribed. Ignoring message:', (receivedData?.event ?? receivedData));
          return;
        }

        console.log("Received WebSocket message: ", receivedData?.event);

        // Handle compressed multi-chunk messages (on subscribe)
        if (receivedData.compressed && receivedData.compressedChunk) {
          const { snapshotBatchIndex, compressedChunkIndex, compressedChunkTotal, compressedChunk } = receivedData;

          if (!compressedChunksRef.current.has(snapshotBatchIndex)) {
            compressedChunksRef.current.set(snapshotBatchIndex, {
              chunks: new Array(compressedChunkTotal).fill(null),
              total: compressedChunkTotal,
              received: 0
            });
          }

          const batch = compressedChunksRef.current.get(snapshotBatchIndex);

          // Only count this chunk if we haven't received it before
          if (batch.chunks[compressedChunkIndex] === null) {
            batch.chunks[compressedChunkIndex] = compressedChunk;
            batch.received++;
            console.log(`Received compressed chunk ${compressedChunkIndex + 1}/${compressedChunkTotal} for batch ${snapshotBatchIndex} (total received: ${batch.received})`);
          }

          // Check if we have all chunks
          if (batch.received === batch.total) {
            console.log(`All chunks received for batch ${snapshotBatchIndex}, decompressing...`);
            try {
              // Reassemble and decompress
              const fullCompressed = batch.chunks.join('');
              // Convert base64 string to Uint8Array
              const binaryString = atob(fullCompressed);
              const bytes = new Uint8Array(binaryString.length);
              for (let i = 0; i < binaryString.length; i++) {
                bytes[i] = binaryString.charCodeAt(i);
              }
              const decompressed = pako.ungzip(bytes, { to: 'string' });
              const data = JSON.parse(decompressed);

              dispatch({ type: 'INITIAL_SNAPSHOT', payload: data });
              console.log(`Successfully decompressed and dispatched ${data.length} devices for batch ${snapshotBatchIndex}`);
              compressedChunksRef.current.delete(snapshotBatchIndex); // Clean up
              // Set visibleDeviceIds based on subscription type
              const newIds = data.map(device => device.coreid);
              setVisibleDeviceIds((prevIds) => [...new Set([...prevIds, ...newIds])]); // Ensure no duplicates
            } catch (error) {
              console.error('Error decompressing chunk data:', error, {
                batchIndex: snapshotBatchIndex,
                chunksReceived: batch.received,
                totalChunks: batch.total,
                compressedLength: batch.chunks.join('').length
              });
            }
          }
          return;
        }
        // Handle aggregated_update message (subsequent updates)
        else if (receivedData.event === "aggregated_update") {
          const { coreid, events } = receivedData;
          if (!coreid || !events) {
            console.warn('Received message is missing expected fields:', receivedData);
            return;
          }
          dispatch({ type: 'AGGREGATED_UPDATE', payload: receivedData });
        }
      } catch (error) {
        console.error('Error processing WebSocket message:', error);
      }
    };

    socket.onclose = (event) => {
      console.log('WebSocket closed:', { code: event.code, reason: event.reason });
      isConnectingRef.current = false;
      setWs(null); // Reset ws to null when connection closes
      // If it closes right after trying to connect, assume auth failed
      // You can refine this by tracking a "justAttemptedConnect" flag/time
      if (!getValidStoredToken()) onAuthExpired?.();
      if (socket._heartbeat) clearInterval(socket._heartbeat);
    };

    socket.onerror = (error) => {
      console.error('WebSocket error:', error);
      isConnectingRef.current = false;
      // Often an auth failure on $connect shows up as error+close; be safe:
      if (!getValidStoredToken()) onAuthExpired?.();
    };
  };

  const handleSubscribe = (deviceIds, subscribeAll, initializeAll) => {
    if (isSubscribed) {
      console.log('Already subscribed. Skip duplicate subscribe request.');
      return;
    }
    if (!ws || ws.readyState !== WebSocket.OPEN) {
      console.log('WebSocket is not open. Cannot subscribe.');
      return;
    }

    const subscribeMessage = {
      action: "/subscribe",
      subscribeAll: subscribeAll,
      coreids: !subscribeAll ? deviceIds.split(',').map(id => id.trim()) : undefined,
      queryInit: initializeAll
    };

    console.log('Sending subscription message:', subscribeMessage);
    ws.send(JSON.stringify(subscribeMessage));

    markSubscribed(true);
    setSubscribeAll(!!subscribeAll);

    // Update visibleDeviceIds based on the subscription type
    const ids = subscribeAll ? [] : deviceIds.split(',').map(id => id.trim());
    setVisibleDeviceIds(ids);
  };

  // Function to disconnect the WebSocket
  const disconnectWebSocket = () => {
    if (ws && ws.readyState === WebSocket.OPEN) {
      console.log('Closing WebSocket connection...');
      ws.onclose = (event) => {
        console.log('WebSocket closed:', { code: event.code, reason: event.reason });
        setWs(null); // Reset ws to null only when the connection is actually closed
        if (ws._heartbeat) clearInterval(ws._heartbeat);
      };
      ws.close(); // Close WebSocket connection
    }
  };

  // Handle unsubscribe action
  const handleUnsubscribe = () => {
    if (isSubscribed && ws && ws.readyState === WebSocket.OPEN) {
      console.log('Unsubscribing...');

      // Send unsubscribe message to server
      const unsubscribeMessage = {
        action: "/unsubscribe"
      };

      console.log('Sending unsubscribe message:', unsubscribeMessage);
      ws.send(JSON.stringify(unsubscribeMessage));

      markSubscribed(false);
      setSubscribeAll(false);
      setVisibleDeviceIds([]); // Clear visible devices on unsubscribe
      // Don't disconnect the WebSocket - keep it alive for quick resubscription
    }
  };

  const handleLogoutAndCloseSocket = () => {
    disconnectWebSocket();
    onAuthExpired(); // Tell App.js to clear token + unauthenticate
  };

  // Intentionally run once on mount to initiate initial connection.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    // Establish WebSocket connection on mount
    connectWebSocket();

    // Cleanup function to close WebSocket when component unmounts
    return () => {
      disconnectWebSocket();
    };
    // mount-only; intentional single connect
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Handle card selection for details panel
  const handleCardSelect = useCallback((device) => {
    setSelectedDeviceId(device.coreid);
  }, []);

  // Handle closing the details panel
  const handleClosePanel = useCallback(() => {
    setSelectedDeviceId(null);
  }, []);


  return (
    <div className={`Dashboard ${animationsEnabled ? '' : 'animations-off'}`.trim()}>
      <h1>System State Dashboard</h1>
      {/* Controls Container - Sticky */}
      <div className="controls-container">
        {/* Subscription Section */}
        <div className="subscription-section">
          <form className="subscription-form" onSubmit={(e) => {
            e.preventDefault();
            handleSubscribe(deviceIds, subscribeAllDevices, initializeAll);
          }}>
            <div className="form-group">
              <button
                type="button"
                className="btn btn-secondary"
                disabled={isSubscribed}
                onClick={() => setShowDeviceSelector(true)}
              >
                {deviceIds ? 'Edit Selection' : 'Select Devices'}
              </button>
            </div>

            <div className="checkbox-group">
              <input
                type="checkbox"
                id="subscribe-all"
                checked={subscribeAllDevices}
                onChange={() => setSubscribeAllDevices(!subscribeAllDevices)}
                disabled={isSubscribed}
              />
              <label htmlFor="subscribe-all">Subscribe to all devices</label>
            </div>

            <div className="checkbox-group">
              <input
                type="checkbox"
                id="initialize-all"
                checked={initializeAll}
                onChange={() => setInitializeAll(!initializeAll)}
                disabled={isSubscribed}
              />
              <label htmlFor="initialize-all">Initialize all device states</label>
            </div>

            <div className="button-group">
              <button
                type="button"
                className={`btn ${isSubscribed ? 'btn-secondary' : ''}`.trim()}
                onClick={(e) => {
                  e.preventDefault();
                  if (isSubscribed) {
                    handleUnsubscribe();
                  } else {
                    handleSubscribe(deviceIds, subscribeAllDevices, initializeAll);
                  }
                }}
                disabled={!ws || ws.readyState !== WebSocket.OPEN}
              >
                {isSubscribed ? 'Unsubscribe' : 'Subscribe'}
              </button>
              <button type="button" className="btn btn-logout" onClick={handleLogoutAndCloseSocket}>
                Logout
              </button>
              <button
                type="button"
                className="btn btn-secondary"
                onClick={() => setShowHelp(true)}
              >
                Help
              </button>
              <div className="connection-status">
                <span>Websocket</span>
                <span>{ws && ws.readyState === WebSocket.OPEN ? 'connected' : 'connecting...'}</span>
              </div>
            </div>
          </form>
        </div>

        {/* Filter / Sort Controls */}
        <div className="filter-section">
          <div className="filter-controls">
            <div className="form-group">
              <label>Search (deviceid / system type)</label>
              <input
                type="text"
                value={filterText}
                onChange={e => setFilterText(e.target.value)}
                placeholder="Search devices..."
              />
            </div>
            <div className="form-group">
              <label>Status</label>
              <select value={filterStatus} onChange={e => setFilterStatus(e.target.value)}>
                <option value="any">Any</option>
                <option value="online">Online</option>
                <option value="offline">Offline</option>
                {/*<option value="stale">Stale</option>*/}
              </select>
            </div>
            <div className="form-group">
              <label>System Type</label>
              <MultiSelect
                options={Object.values(SYSTEM_TYPES).filter((v, i, a) => a.indexOf(v) === i).sort()}
                value={filterSystemType}
                onChange={setFilterSystemType}
                placeholder="All Types"
              />
            </div>
            <div className="form-group">
              <label>Product Family</label>
              <select value={filterProductFamily} onChange={e => {
                setFilterProductFamily(e.target.value);
                // Reset firmware filter when product family changes
                setFilterFirmware([]);
              }}>
                <option value="any">Any</option>
                <option value="Ori v1">Ori v1</option>
                <option value="Ori v2">Ori v2</option>
              </select>
            </div>
            <div className="form-group">
              <label>Firmware</label>
              <MultiSelect
                options={useMemo(() => {
                  const firmwareSet = new Set();
                  Object.values(devices).forEach(d => {
                    if (d.fw_version && d.productid) {
                      const productName = PRODUCT_NAMES[Number(d.productid)] || 'Unknown';
                      // Only include firmware versions that match the selected product family
                      if (filterProductFamily === 'any') {
                        // Include product name in the option label when no specific family is selected
                        firmwareSet.add(`v${d.fw_version} ${productName}`);
                      } else if (productName === filterProductFamily) {
                        firmwareSet.add(`v${d.fw_version}`);
                      }
                    }
                  });
                  return Array.from(firmwareSet).sort((a, b) => {
                    // Extract version numbers after 'v' character and sort numerically
                    const extractVersion = (str) => {
                      const match = str.match(/v(\d+)/);
                      return match ? parseInt(match[1]) : NaN;
                    };

                    const numA = extractVersion(a);
                    const numB = extractVersion(b);

                    if (!isNaN(numA) && !isNaN(numB)) {
                      return numB - numA; // Descending order (newest first)
                    }
                    return b.localeCompare(a); // Descending alphabetically as fallback
                  });
                }, [devices, filterProductFamily])}
                value={filterFirmware}
                onChange={setFilterFirmware}
                placeholder="All Versions"
              />
            </div>
            <div className="form-group">
              <label>Sort By</label>
              <select value={sortBy} onChange={e => setSortBy(e.target.value)}>
                <option value="last_heard">Last Heard</option>
                <option value="coreid">Device ID</option>
                <option value="system_type">System Type</option>
                <option value="product">Product Family</option>
                <option value="movement">Movement State</option>
              </select>
            </div>
            <div className="form-group">
              <label>Direction</label>
              <select value={sortDir} onChange={e => setSortDir(e.target.value)}>
                <option value="asc">Asc</option>
                <option value="desc">Desc</option>
              </select>
            </div>
            {/*<div className="checkbox-group">
              <input 
                type="checkbox" 
                id="hide-stale"
                checked={hideStale} 
                onChange={e => setHideStale(e.target.checked)} 
              />
              <label htmlFor="hide-stale">Hide stale</label>
            </div>*/}
            <button
              type="button"
              className="btn btn-secondary"
              onClick={() => {
                setFilterText('');
                setFilterStatus('any');
                setFilterSystemType([]);
                setFilterProductFamily('any');
                setFilterFirmware([]);
                setHideStale(false);
                setSortBy('last_heard');
                setSortDir('desc');
              }}
            >
              Reset Filters
            </button>
            <div className="system-count">
              <span>{systemCount.filtered}/{systemCount.total} systems</span>
            </div>
          </div>
        </div>
      </div>

      {/* Scrollable Content Area */}
      <div className="dashboard-content">
        {useMemo(() => {
          const all = Object.values(devices);
          const visibleDevices = all.filter(d => subscribeAll || visibleDeviceIds.includes(d.coreid));
          const filtered = visibleDevices.filter(d => {
            const sysType = resolveSystemType(d);
            const status = d.aggregatedData["k/status"] !== "offline" ? "online" : "offline";
            const isStale = false; // Staleness currently disabled; compute if needed later.
            const productName = PRODUCT_NAMES[Number(d.productid)] || 'Unknown';

            if (hideStale && isStale) return false;
            if (filterStatus !== 'any') {
              if (filterStatus === 'stale' && !isStale) return false;
              if (filterStatus !== 'stale' && status !== filterStatus) return false;
            }
            // System Type filter - OR logic (if array has items, device must match at least one)
            if (filterSystemType.length > 0 && !filterSystemType.includes(sysType)) return false;

            if (filterProductFamily !== 'any' && productName !== filterProductFamily) return false;

            // Firmware filter - OR logic (if array has items, device must match at least one)
            if (filterFirmware.length > 0) {
              const matchesFirmware = filterFirmware.some(filterValue => {
                // Extract version number from filter value (e.g., "v123" or "v123 Ori v1" -> "123")
                const match = filterValue.match(/v(\d+)/);
                const filterVersion = match ? match[1] : null;
                return filterVersion && d.fw_version === filterVersion;
              });
              if (!matchesFirmware) return false;
            }

            if (filterText) {
              const ft = filterText.toLowerCase();
              if (!d.coreid.toLowerCase().includes(ft) && !sysType.toLowerCase().includes(ft)) return false;
            }
            return true;
          });

          // Update system count for display (doing this here to avoid extra state updates)
          if (setSystemCount) {
            setTimeout(() => setSystemCount({ filtered: filtered.length, total: visibleDevices.length }), 0);
          }

          const sorted = filtered.sort((a, b) => {
            const dir = sortDir === 'asc' ? 1 : -1;
            if (sortBy === 'coreid') {
              return a.coreid.localeCompare(b.coreid) * dir;
            } else if (sortBy === 'system_type') {
              const aType = resolveSystemType(a);
              const bType = resolveSystemType(b);
              const cmp = aType.localeCompare(bType);
              if (cmp !== 0) return cmp * dir;
              return a.coreid.localeCompare(b.coreid) * dir;
            } else if (sortBy === 'product') {
              const aProd = PRODUCT_NAMES[parseInt(a.productid, 10)] || 'Unknown';
              const bProd = PRODUCT_NAMES[parseInt(b.productid, 10)] || 'Unknown';
              const cmp = aProd.localeCompare(bProd);
              if (cmp !== 0) return cmp * dir;
              return a.coreid.localeCompare(b.coreid) * dir;
            } else if (sortBy === 'movement') {
              const aMove = a.movement.main.moving || 'zzz';
              const bMove = b.movement.main.moving || 'zzz';
              const cmp = aMove.localeCompare(bMove);
              if (cmp !== 0) return cmp * dir * -1; // Reverse order for movement - Descending should show moving systems first
              return a.coreid.localeCompare(b.coreid) * dir;
            } else { // last_heard
              const aTs = a.published_at ? new Date(a.published_at).getTime() : 0;
              const bTs = b.published_at ? new Date(b.published_at).getTime() : 0;
              if (aTs === bTs) return a.coreid.localeCompare(b.coreid) * dir;
              return (aTs - bTs) * dir;
            }
          });
          return (
            <div className="devices-grid">
              {sorted.map((d, index) => {
                const isSelected = selectedDeviceId === d.coreid;
                return (
                  <DeviceCard
                    key={d.coreid}
                    device={d}
                    selected={isSelected}
                    onSelect={() => handleCardSelect(d)}
                  />
                );
              })}
            </div>
          );
        }, [devices, visibleDeviceIds, subscribeAll, filterText, filterStatus, filterSystemType, filterProductFamily, filterFirmware, hideStale, sortBy, sortDir, selectedDeviceId, handleCardSelect])}
      </div>

      {/* Device Details Panel */}
      {selectedDeviceId && (
        <DeviceDetailsPanel
          device={devices[selectedDeviceId]}
          onClose={handleClosePanel}
        />
      )}

      {/* Toast notifications container */}
      <ToastContainer />
      {showHelp && (
        <HelpModal
          onClose={() => setShowHelp(false)}
          animationsEnabled={animationsEnabled}
          onToggleAnimations={() => setAnimationsEnabled(a => !a)}
        />
      )}
      {showDeviceSelector && (
        <DeviceSelectorModal
          existingIds={deviceIds}
          ws={ws}
          onClose={() => setShowDeviceSelector(false)}
          onApply={(idsArray) => {
            const joined = idsArray.join(',');
            setDeviceIds(joined);
            setShowDeviceSelector(false);
          }}
        />
      )}
    </div>
  );
};

export default Dashboard;
